import React from 'react';
import styled from 'styled-components';
import {
  Button,
  Form,
  Input,
  RadioButton,
  RadioGroup,
  Select,
  SelectOption,
  Text,
  message,
  RadioGroupProps,
} from 'components';
import { RetsController } from 'api';
import { Country } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { ListingLabelContext } from 'contexts';

export type OnboardingMemberIdFormValues = {
  memberId: string;
  boardId: string;
  country: Country;
};

export type OnboardingMemberIdFormProps = {
  initialValues?: OnboardingMemberIdFormValues;
  inputDisabled?: boolean;
  hideSubmit?: boolean;
  submitLabel?: string;
  onSubmit?: (values: OnboardingMemberIdFormValues) => void;
  working?: boolean;
};

export const OnboardingMemberIdForm: React.FC<
  OnboardingMemberIdFormProps
> = props => {
  const {
    inputDisabled = false,
    hideSubmit = false,
    initialValues,
    onSubmit,
    submitLabel = 'Next',
    working,
  } = props;

  const [loading, setLoading] = React.useState(false);

  const [boards, setBoards] = React.useState<
    { _id: string; name: string; board: string }[]
  >([]);

  const [country, setCountry] = React.useState<Country>(
    initialValues?.country || Country.USA,
  );

  const { getBoardName } = React.useContext(ListingLabelContext);
  const [memberIdForm] = Form.useForm();

  React.useEffect(() => {
    const fetchBoards = async () => {
      setLoading(true);

      try {
        const data = await RetsController.getBoards(country);
        setBoards(data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorReporter.error(err);
        message.error('Could not load boards. Please reload the page.');
      }
    };

    fetchBoards();
  }, [country]);

  const buildBoardElement = (
    board: string,
    boardId: string,
    fullName: string,
  ) => {
    const boardName = getBoardName(board, false); // Will be added later when we add consolidation logic for selecting an aliased board

    return (
      <SelectOption key={boardId} value={boardId} label={boardName}>
        <Text>{boardName}</Text>
        <LongNameText>{fullName}</LongNameText>
      </SelectOption>
    );
  };

  const handleFormOnFinish = (values: OnboardingMemberIdFormValues) => {
    if (!onSubmit) {
      return;
    }

    onSubmit(values);
  };

  const handleCountryChange: RadioGroupProps['onChange'] = e => {
    const updatedCountry = e.target.value;
    if (country && country !== updatedCountry) {
      // undefined is the default empty state as per antd forms
      memberIdForm.setFieldsValue({ boardId: undefined });
    }
    setCountry(updatedCountry);
  };

  return (
    <Form
      name="onboardingMemberIdForm"
      initialValues={initialValues}
      onFinish={handleFormOnFinish}
      form={memberIdForm}
    >
      <CenteredFormItem name="country" initialValue={country}>
        <RadioGroup
          value={country}
          disabled={loading || inputDisabled}
          onChange={handleCountryChange}
          buttonJustify="center"
          dataCy="onboardingMemberIdForm-radioGroup-country"
        >
          <RadioButton name="country" value={Country.USA}>
            US
          </RadioButton>
          <RadioButton name="country" value={Country.CAN}>
            Canada
          </RadioButton>
        </RadioGroup>
      </CenteredFormItem>

      <Form.Item
        name="boardId"
        rules={[{ required: true, message: 'Please select a board' }]}
      >
        <Select
          placeholder="Association / MLS Name"
          loading={loading}
          disabled={loading || inputDisabled}
          showSearch
          filterOption={(input, option) =>
            getBoardName(option.label as string, false)
              .toUpperCase()
              .includes(input.toUpperCase())
          }
          data-cy="onboardingMemberIdForm-select-board"
        >
          {boards.map(({ _id, board, name }) =>
            buildBoardElement(board, _id, name),
          )}
        </Select>
      </Form.Item>

      <Form.Item
        name="memberId"
        rules={[{ required: true, message: 'Please enter a member number' }]}
      >
        <Input
          placeholder="Member number or ID"
          disabled={!onSubmit || inputDisabled}
          data-cy="onboardingMemberIdForm-input-memberNumber"
        />
      </Form.Item>

      {!hideSubmit && (
        <Form.Item>
          <Button
            block
            type="primary"
            htmlType="submit"
            disabled={!onSubmit || working}
            loading={working}
            data-cy="submit-button"
          >
            {submitLabel}
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

const CenteredFormItem = styled(Form.Item)`
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
`;

const LongNameText = styled(Text).attrs({ type: 'secondary' })`
  margin-left: 5px;
`;
